import { useEffect } from "react";
import { Facebook, Instagram, Youtube, Music2, ExternalLink } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const NETS = [
  { key: "facebook", label: "Facebook", icon: Facebook },
  { key: "instagram", label: "Instagram", icon: Instagram },
  { key: "tiktok", label: "TikTok", icon: Music2 },
  { key: "youtube", label: "YouTube", icon: Youtube },
];

export const SocialLinks = () => {
  const { site } = useSite() || {};
  const items = NETS.filter((n) => site?.social?.[n.key]);
  if (!items.length) return null;
  return (
    <div className="flex gap-3" data-testid="social-links">
      {items.map(({ key, label, icon: Icon }) => (
        <a key={key} href={site.social[key]} target="_blank" rel="noopener noreferrer" aria-label={label}
          className="grid h-10 w-10 place-items-center rounded-sm border border-line text-green hover:border-brand transition-colors"
          data-testid={`social-${key}-link`}><Icon size={18} /></a>
      ))}
    </div>
  );
};

const TikTokEmbed = ({ url }) => {
  const user = (url.match(/@([\w.]+)/) || [])[1];
  useEffect(() => {
    if (!user) return;
    const s = document.createElement("script");
    s.src = "https://www.tiktok.com/embed.js";
    s.async = true;
    document.body.appendChild(s);
    return () => { s.remove(); };
  }, [user]);
  if (!user) return null;
  return (
    <blockquote className="tiktok-embed" cite={`https://www.tiktok.com/@${user}`} data-unique-id={user} data-embed-type="creator" style={{ maxWidth: 500, minWidth: 288 }}>
      <section><a target="_blank" rel="noopener noreferrer" href={`https://www.tiktok.com/@${user}`}>@{user}</a></section>
    </blockquote>
  );
};

const ProfileCard = ({ net, url }) => {
  const Icon = net.icon;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="card-dark flex items-center justify-between p-6" data-testid={`social-card-${net.key}`}>
      <span className="flex items-center gap-3"><Icon className="text-brand" /> <span className="font-display text-xl uppercase">Síguenos en {net.label}</span></span>
      <ExternalLink size={18} className="text-mute" />
    </a>
  );
};

export const FollowBar = () => {
  const { site } = useSite() || {};
  const fb = site?.social?.facebook || "https://www.facebook.com/profile.php?id=61578026697560";
  return (
    <section className="relative overflow-hidden border-b-4 border-gold bg-green2 text-white" aria-label="Síguenos en Facebook" data-testid="follow-bar">
      <div className="absolute -left-10 top-1/2 h-40 w-40 -translate-y-1/2 rounded-full bg-gold/20 blur-2xl" aria-hidden="true" />
      <div className="container-x relative flex flex-col items-center gap-4 py-6 text-center sm:flex-row sm:gap-6 sm:py-7 sm:text-left">
        <img src="/logo.png" alt="" className="h-20 w-20 shrink-0 rounded-full border-4 border-gold shadow-lg sm:h-24 sm:w-24" />
        <div className="flex-1">
          <p className="font-display text-2xl font-extrabold leading-tight sm:text-3xl">Síguenos en <span className="text-gold">Facebook</span></p>
          <p className="mt-1 text-sm text-white/85 sm:text-base">Consejos de suspensión, trabajos del taller y testimonios de clientes. ¡No te pierdas nada!</p>
        </div>
        <a href={fb} target="_blank" rel="noopener noreferrer" data-testid="follow-bar-facebook-btn"
          className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-[#1877F2] px-7 py-4 text-base font-extrabold text-white shadow-xl ring-4 ring-white/20 transition-transform hover:-translate-y-0.5 sm:text-lg attn">
          <Facebook size={22} /> Seguir la página
        </a>
      </div>
    </section>
  );
};

export const SocialFeeds = () => {
  const { site } = useSite() || {};
  const social = site?.social || {};
  const fb = social.facebook || "https://www.facebook.com/profile.php?id=61578026697560";
  const others = NETS.filter((n) => social[n.key] && n.key !== "facebook");
  return (
    <section className="container-x py-16" aria-labelledby="social-title" data-testid="social-feeds-section">
      <div className="relative overflow-hidden rounded-2xl bg-green2 text-white">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-gold/20 blur-2xl" aria-hidden="true" />
        <div className="relative flex flex-col items-center gap-6 p-8 text-center md:flex-row md:p-10 md:text-left">
          <img src="/logo.png" alt="Logo Taller de Suspensiones La Gringa" className="h-24 w-24 shrink-0 rounded-full border-4 border-gold shadow-lg" />
          <div className="flex-1">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-gold">Redes sociales</p>
            <h2 id="social-title" className="mt-2 text-2xl sm:text-3xl font-extrabold">Síguenos en Facebook</h2>
            <p className="mt-2 text-white/80">Consejos de suspensión, trabajos del taller y testimonios de clientes. ¡Dale “Seguir” y no te pierdas nada!</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row md:flex-col">
            <a href={fb} target="_blank" rel="noopener noreferrer" data-testid="social-follow-facebook-btn"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#1877F2] px-6 py-3 font-bold text-white shadow-md transition-transform hover:-translate-y-0.5">
              <Facebook size={18} /> Seguir la página
            </a>
            {others.map(({ key, label, icon: Icon }) => (
              <a key={key} href={social[key]} target="_blank" rel="noopener noreferrer" data-testid={`social-follow-${key}-btn`}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/30 px-6 py-3 font-bold text-white hover:bg-white/10">
                <Icon size={18} /> {label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
